/**
 * 매핑 편집 동작 — 편집 중인 매핑을 받아 새 매핑을 돌려주는 순수 함수.
 *
 * 폼은 값을 직접 조립하지 않고 이 함수들을 부른다. 그래서 규칙이 한 곳에 있다. 예를 들어 자세 종류를
 * 바꾸면 열 수가 바뀌고, 손 X 축을 바꾸면 Y 축이 직교하도록 맞춘다.
 *
 * 자동으로 채우지 않는다(BE 재설계 §4). 채널을 고르면 열은 비어 있고, 원본 선언은 사람이
 * `선언값 사용` 을 누를 때만 들어간다.
 *
 * Owns:
 * - 손 구성 · 역할 · 구성요소 · 손 축 · 기준 변환 · 카메라 · 시간 이동의 편집 규칙, 원본 선언의 해석과 비교,
 *   두 매핑의 같음.
 *
 * Does not own:
 * - 편집 상태 · 요청 — `mappingEditor.ts`.
 */
import { textOrNull } from "@/shared/utils/text";
import type { PoseDeclaration } from "../domain/episodeInspect";
import {
  emptyAction,
  emptyHand,
  emptySelection,
  HAND_AXES,
  orientationColumnCount,
  LENGTH_UNITS,
  type AngleUnit,
  type Selection,
  type EulerFrame,
  type EulerOrder,
  type Gripper,
  type Axis,
  type HandRole,
  type HandRoleName,
  type HandRotation,
  type Side,
  type Mapping,
  type OrientationKind,
  type Orientation,
  type Position,
  type LengthUnit,
  type QuaternionOrder,
  type ReferenceTransform,
  type ReferenceTransformKind,
} from "../domain/mapping";
import { areAxesOrthogonal, axisVector, referenceMatrix } from "./rotationMath";
import { cameraName } from "./mappingPath";

/** 손 구성 — BE 는 `single` 하나 또는 `left` / `right` 를 중복 없이 받는다. */
export const HAND_LAYOUTS = ["single", "left", "right", "both"] as const;
export type HandLayout = (typeof HAND_LAYOUTS)[number];

const LAYOUT_SIDES: Readonly<Record<HandLayout, readonly Side[]>> = {
  single: ["single"],
  left: ["left"],
  right: ["right"],
  both: ["left", "right"],
};

/** 지금 손 구성. 손이 없거나 BE 가 받지 않는 조합이면 `null` 이다. */
export function handLayout(mapping: Mapping): HandLayout | null {
  const sides = mapping.hands.map((hand) => hand.side).join(",");
  switch (sides) {
    case "single":
      return "single";
    case "left":
      return "left";
    case "right":
      return "right";
    case "left,right":
    case "right,left":
      return "both";
    default:
      return null;
  }
}

/**
 * 손 구성을 바꾼다. 남는 side 의 값은 그대로 두고, 새 side 는 빈 관측으로 시작한다. 관측은 필수다.
 * 양손은 왼손 · 오른손 순서다.
 */
export function setHandLayout(mapping: Mapping, layout: HandLayout): Mapping {
  const hands = LAYOUT_SIDES[layout].map(
    (side) => mapping.hands.find((hand) => hand.side === side) ?? emptyHand(side),
  );
  return { ...mapping, hands };
}

/**
 * 손 하나의 역할을 바꾼다. action 이 없으면 바꾸지 않는다. `update` 는 관측과 action 이 같이 가진 칸만
 * 바꾼다. 그래서 action 의 `motion` 은 그대로 남긴다.
 */
export function updateRole(
  mapping: Mapping,
  handIndex: number,
  role: HandRoleName,
  update: (current: HandRole) => HandRole,
): Mapping {
  return {
    ...mapping,
    hands: mapping.hands.map((hand, index) => {
      if (index !== handIndex) {
        return hand;
      }
      if (role === "observation") {
        return { ...hand, observation: update(hand.observation) };
      }
      return hand.action === null
        ? hand
        : { ...hand, action: { ...update(hand.action), motion: hand.action.motion } };
    }),
  };
}

/** action 을 쓸지. 끄면 action 값을 지운다. 관측을 action 으로 복사하지 않는다. */
export function setActionEnabled(mapping: Mapping, handIndex: number, enabled: boolean): Mapping {
  return {
    ...mapping,
    hands: mapping.hands.map((hand, index) =>
      index === handIndex
        ? { ...hand, action: enabled ? (hand.action ?? emptyAction()) : null }
        : hand,
    ),
  };
}

// ── 구성요소 ───────────────────────────────────────────────────────────────

/**
 * 채널을 바꾸면 열을 비운다. 다른 채널의 같은 번호는 다른 값이다. `null` 은 채널을 고르지 않은 것이다.
 * 열 수는 그대로다 — 구성요소와 자세 종류가 정한다.
 */
function withChannel(selection: Selection, channel: string | null): Selection {
  return selection.channel === channel
    ? selection
    : emptySelection(channel, selection.columns.length);
}

export function setSelectionColumn(
  selection: Selection,
  index: number,
  column: number | null,
): Selection {
  return {
    ...selection,
    columns: selection.columns.map((current, at) => (at === index ? column : current)),
  };
}

/** 위치 채널. `null` 은 채널 · 열을 비운다. 단위 · 근거는 채널을 바꿔도 남긴다. */
export function setPositionChannel(position: Position, channel: string | null): Position {
  return { ...position, source: withChannel(position.source, channel) };
}

/** 자세 채널. `null` 은 채널 · 열을 비운다. 표현 · 근거는 채널을 바꿔도 남긴다. */
export function setOrientationChannel(
  orientation: Orientation,
  channel: string | null,
): Orientation {
  return { ...orientation, source: withChannel(orientation.source, channel) };
}

/**
 * 자세 표현 종류. 열 수를 새 종류에 맞추고(앞 열은 남긴다) 다른 종류의 설정은 지운다. 저장된 JSON 에
 * 쓰지 않는 값이 남지 않게 한다. 근거는 남긴다.
 */
export function setOrientationKind(orientation: Orientation, kind: OrientationKind): Orientation {
  const count = orientationColumnCount(kind);
  const columns = Array.from(
    { length: count },
    (_, index) => orientation.source.columns[index] ?? null,
  );
  return {
    ...orientation,
    source: { ...orientation.source, columns },
    kind,
    quaternion_order: kind === "quaternion" ? orientation.quaternion_order : null,
    normalize_quaternion: kind === "quaternion" ? orientation.normalize_quaternion : false,
    euler_order: kind === "euler" ? orientation.euler_order : null,
    euler_frame: kind === "euler" ? orientation.euler_frame : null,
    angle_unit: kind === "euler" ? orientation.angle_unit : null,
  };
}

/** 그리퍼 채널. `null` 은 채널 · 열을 비운다. 물리량 · 단위 · 끝점 · 근거는 채널을 바꿔도 남긴다. */
export function setGripperChannel(gripper: Gripper, channel: string | null): Gripper {
  return { ...gripper, source: withChannel(gripper.source, channel) };
}

// ── 손 축 ──────────────────────────────────────────────────────────────────

/** `axis` 와 직교하는 축. Y 드롭다운의 선택지다. */
export function orthogonalAxes(axis: Axis): readonly Axis[] {
  return HAND_AXES.filter((candidate) => areAxesOrthogonal(axis, candidate));
}

/** 손 Z 축 = X × Y. 예) `+x` · `+y` → `+z`, `-z` · `+x` → `-y`. 평행하면 `null` 이다. */
export function derivedZAxis(xAxis: Axis, yAxis: Axis): Axis | null {
  if (!areAxesOrthogonal(xAxis, yAxis)) {
    return null;
  }
  const [ax, ay, az] = axisVector(xAxis);
  const [bx, by, bz] = axisVector(yAxis);
  const z = [ay * bz - az * by, az * bx - ax * bz, ax * by - ay * bx];
  const index = z.findIndex((component) => component !== 0);
  return `${z[index] > 0 ? "+" : "-"}${"xyz"[index]}` as Axis;
}

/**
 * 손 X 축을 바꾼다. Y 축이 평행해지면 첫 직교 축으로 옮긴다. 추가 각도는 그대로 둔다 — 새 손 로컬 축에
 * 같은 각도를 적용한다(BE 재설계 §15).
 */
export function setHandXAxis(rotation: HandRotation, xAxis: Axis): HandRotation {
  const yAxis = areAxesOrthogonal(xAxis, rotation.y_axis)
    ? rotation.y_axis
    : orthogonalAxes(xAxis)[0];
  return { ...rotation, x_axis: xAxis, y_axis: yAxis };
}

/** 손 Y 축을 바꾼다. X 와 평행한 축은 받지 않는다(화면이 선택지로 막는다). */
export function setHandYAxis(rotation: HandRotation, yAxis: Axis): HandRotation {
  return areAxesOrthogonal(rotation.x_axis, yAxis) ? { ...rotation, y_axis: yAxis } : rotation;
}

/** 로컬 X · Y · Z 중 하나의 최종 각도(도)를 정한다. 버튼도 이 값을 바꾼다. 클릭을 누적하지 않는다. */
export function setHandDegree(
  rotation: HandRotation,
  axisIndex: 0 | 1 | 2,
  degrees: number,
): HandRotation {
  const next: [number, number, number] = [...rotation.degrees];
  next[axisIndex] = degrees;
  return { ...rotation, degrees: next };
}

// ── 기준 변환 ──────────────────────────────────────────────────────────────

/**
 * 기준 변환의 종류를 바꾼다. identity 로 바꾸면 원본 프레임 · 회전 · 이동 칸을 비운다(BE 는 identity 에 그 칸을
 * 받지 않는다). 근거는 둔다. 폼이 다시 만들 수 없는 MCP · JSON 입력이기 때문이다.
 */
export function setReferenceTransformKind(
  spec: ReferenceTransform,
  kind: ReferenceTransformKind,
): ReferenceTransform {
  if (kind === "fixed") {
    return { ...spec, kind };
  }
  return {
    ...spec,
    kind,
    source_frame: null,
    quaternion_wxyz: [null, null, null, null],
    x_m: null,
    y_m: null,
    z_m: null,
  };
}

/** quaternion 한 성분(w · x · y · z 순서)을 바꾼다. 정규화하지 않는다. */
export function setReferenceQuaternion(
  spec: ReferenceTransform,
  index: 0 | 1 | 2 | 3,
  value: number | null,
): ReferenceTransform {
  const next: [number | null, number | null, number | null, number | null] = [
    ...spec.quaternion_wxyz,
  ];
  next[index] = value;
  return { ...spec, quaternion_wxyz: next };
}

export type QuaternionLength = { readonly length: number; readonly unit: boolean };

/**
 * quaternion 길이와 단위 여부. 빈 성분이 있으면 `null` 이다. 단위 판정은 3D 계산(`referenceMatrix`)과 같은
 * 함수라 폼 경고와 3D 가 어긋나지 않는다.
 */
export function referenceQuaternionLength(spec: ReferenceTransform): QuaternionLength | null {
  const [w, x, y, z] = spec.quaternion_wxyz;
  if (w === null || x === null || y === null || z === null) {
    return null;
  }
  return { length: Math.hypot(w, x, y, z), unit: referenceMatrix([w, x, y, z]) !== null };
}

// ── 카메라 · 시간 ──────────────────────────────────────────────────────────

/**
 * 카메라 폼의 줄 하나. `index` 는 줄이 가리키는 첫 매핑 카메라 번호다. 매핑에 없는 영상 채널은 -1 이다.
 * `indexes` 는 이 줄이 맡은 매핑 카메라 번호 전부다. 영상 채널 줄은 원본이 같은 카메라를 모두 맡는다. 원본이 같은
 * 카메라는 줄을 하나만 가지므로 따로 지울 수 없기 때문이다(MCP · JSON 이 만들 수 있다).
 * `id` 는 줄의 고유 이름이다. 영상 채널 줄은 `video:{source}`, 매핑에만 있는 줄은 `camera:{index}` 다.
 * `name` 은 줄 이름이다(`mappingPath.ts` 의 `cameraName`, 확인 필요 항목 · issue 이름과 같다).
 */
export type CameraRow = {
  readonly source: string | null;
  readonly index: number;
  readonly indexes: readonly number[];
  readonly id: string;
  readonly name: string;
};

/**
 * 카메라 폼의 줄. 이 episode 의 영상 채널을 먼저 놓고, 매핑에만 있는 카메라를 뒤에 놓는다. 매핑에만 있는
 * 카메라는 이 episode 에 없는 원본을 가리키거나 원본을 아직 고르지 않은 것이다. 줄을 지우지 않는다.
 */
export function cameraRows(mapping: Mapping, videoKeys: readonly string[]): CameraRow[] {
  return [
    ...videoKeys.map((source) => {
      const indexes = mapping.cameras.flatMap((camera, at) =>
        camera.source === source ? [at] : [],
      );
      return { source, index: indexes[0] ?? -1, indexes, id: `video:${source}`, name: source };
    }),
    ...mapping.cameras.flatMap((camera, index) => {
      if (camera.source !== null && videoKeys.includes(camera.source)) {
        return [];
      }
      return [
        {
          source: camera.source,
          index,
          indexes: [index],
          id: `camera:${index}`,
          name: cameraName(mapping, index),
        },
      ];
    }),
  ];
}

/** 영상 채널 하나를 카메라로 넣는다. 이미 있으면 그대로다. slot 은 비어 있다 — 사람이 정한다. */
export function addCamera(mapping: Mapping, source: string): Mapping {
  if (mapping.cameras.some((camera) => camera.source === source)) {
    return mapping;
  }
  return { ...mapping, cameras: [...mapping.cameras, { source, slot: null }] };
}

/** 카메라 하나의 slot 이름. 원본을 고르지 않은 카메라도 있어 번호로 가리킨다. */
export function setCameraSlot(mapping: Mapping, index: number, slot: string): Mapping {
  return {
    ...mapping,
    cameras: mapping.cameras.map((camera, at) =>
      at === index ? { ...camera, slot: textOrNull(slot) } : camera,
    ),
  };
}

/** 카메라 줄 하나가 맡은 매핑 카메라를 지운다(`CameraRow.indexes`). */
export function removeCameras(mapping: Mapping, indexes: readonly number[]): Mapping {
  return { ...mapping, cameras: mapping.cameras.filter((_, at) => !indexes.includes(at)) };
}

// ── 원본 선언 ──────────────────────────────────────────────────────────────

/** 원본이 선언한 위치 단위. 매핑이 받지 않는 단위면 `null` 이다. */
export function declaredPositionUnit(declaration: PoseDeclaration): LengthUnit | null {
  return LENGTH_UNITS.find((unit) => unit === declaration.positionUnit) ?? null;
}

export type DeclaredOrientation = {
  readonly kind: OrientationKind;
  readonly quaternion_order: QuaternionOrder | null;
  readonly euler_order: EulerOrder | null;
  readonly euler_frame: EulerFrame | null;
  readonly angle_unit: AngleUnit | null;
};

const EULER_PATTERN = /^euler_(intrinsic_)?(xyz|xzy|yxz|yzx|zxy|zyx)_(deg|rad)$/;

/**
 * 원본이 선언한 회전 표현. BE `compose.py` 가 비교에 쓰는 문자열의 역이다. 모르는 문자열이면 `null` 이다.
 * 예) `quaternion_xyzw` · `euler_xyz_rad`(extrinsic) · `euler_intrinsic_zyx_deg` · `matrix_row_major`.
 */
export function declaredOrientation(declaration: PoseDeclaration): DeclaredOrientation | null {
  const rotation = declaration.rotation;
  const none = { quaternion_order: null, euler_order: null, euler_frame: null, angle_unit: null };
  if (rotation === "quaternion_wxyz" || rotation === "quaternion_xyzw") {
    return {
      ...none,
      kind: "quaternion",
      quaternion_order: rotation === "quaternion_wxyz" ? "wxyz" : "xyzw",
    };
  }
  if (rotation === "matrix_row_major") {
    return { ...none, kind: "matrix" };
  }
  const euler = EULER_PATTERN.exec(rotation);
  if (euler === null) {
    return null;
  }
  return {
    ...none,
    kind: "euler",
    euler_order: euler[2].toUpperCase() as EulerOrder,
    euler_frame: euler[1] === undefined ? "extrinsic" : "intrinsic",
    angle_unit: euler[3] as AngleUnit,
  };
}

/**
 * 원본 선언의 좌표계 이름을 역할에 넣는다. 원본 선언은 원본이 적힌 좌표계를 말한다. 그래서 기준 변환이 fixed 면
 * 기준 프레임이 아니라 기준 변환의 원본 프레임에 넣는다. BE `compose._declaration_issues` 와 같은 대응이다.
 */
export function applyDeclaredFrames(role: HandRole, declaration: PoseDeclaration): HandRole {
  const reference = role.reference_transform;
  return reference.kind === "fixed"
    ? {
        ...role,
        reference_transform: { ...reference, source_frame: declaration.referenceFrame },
        target_frame: declaration.targetFrame,
      }
    : {
        ...role,
        reference_frame: declaration.referenceFrame,
        target_frame: declaration.targetFrame,
      };
}

/** 선언된 회전 표현을 자세에 넣는다. 열은 새 종류의 열 수로 맞춘다. */
export function applyDeclaredOrientation(
  orientation: Orientation,
  declared: DeclaredOrientation,
): Orientation {
  return {
    ...setOrientationKind(orientation, declared.kind),
    quaternion_order: declared.quaternion_order,
    euler_order: declared.euler_order,
    euler_frame: declared.euler_frame,
    angle_unit: declared.angle_unit,
  };
}

/**
 * 폼의 자세를 BE 가 원본 선언과 비교하는 문자열로 적는다. BE `_rotation_label` 과 같은 분기다.
 * 표현을 정하는 칸이 하나라도 비었으면 null 이다. 빈 칸은 다른 값이 아니라 아직 고르지 않은 값이다.
 */
function rotationName(orientation: Orientation): string | null {
  const { kind, euler_order: eulerOrder, euler_frame: eulerFrame, angle_unit: unit } = orientation;
  if (kind === "quaternion") {
    return orientation.quaternion_order === null
      ? null
      : `quaternion_${orientation.quaternion_order}`;
  }
  if (kind === "euler") {
    if (eulerOrder === null || eulerFrame === null || unit === null) {
      return null;
    }
    const frame = eulerFrame === "extrinsic" ? "" : "intrinsic_";
    return `euler_${frame}${eulerOrder.toLowerCase()}_${unit}`;
  }
  return kind === "matrix" ? "matrix_row_major" : null;
}

export type DeclarationField = "reference_frame" | "target_frame" | "position_unit" | "rotation";

/**
 * 역할 폼이 원본 선언과 다른 칸. BE 는 다른 칸이 있으면 재해석 사유를 요구한다. 사유가 없으면 오류
 * (`pose.reinterpretation_required`), 있으면 경고(`pose.reinterpreted`)다. 선언이 없는 채널은 비교하지 않는다.
 * 폼의 빈 칸도 비교하지 않는다. BE 는 완성본만 비교하고, 빈 칸은 필수 칸 확인이 알린다.
 */
export function declarationMismatches(
  role: HandRole,
  declarations: ReadonlyMap<string, PoseDeclaration | null>,
): readonly DeclarationField[] {
  const mismatches = new Set<DeclarationField>();
  const compare = (
    declaration: PoseDeclaration | null | undefined,
    expected: Partial<Record<DeclarationField, string | null>>,
  ): void => {
    if (declaration === null || declaration === undefined) {
      return;
    }
    const observed: Record<DeclarationField, string> = {
      reference_frame: declaration.referenceFrame,
      target_frame: declaration.targetFrame,
      position_unit: declaration.positionUnit,
      rotation: declaration.rotation,
    };
    for (const [field, value] of Object.entries(expected) as [DeclarationField, string | null][]) {
      if (value !== null && observed[field] !== value) {
        mismatches.add(field);
      }
    }
  };
  // BE 는 완성본에서만 비교한다. 위치 · 자세 채널을 모두 골랐을 때만 비교한다.
  const positionChannel = role.position.source.channel;
  const orientationChannel = role.orientation.source.channel;
  if (positionChannel === null || orientationChannel === null) {
    return [];
  }
  // 원본 선언은 원본 좌표계를 말한다. 기준 변환이 fixed 면 그것은 원본 프레임이다.
  const sourceFrame =
    role.reference_transform.kind === "fixed"
      ? role.reference_transform.source_frame
      : role.reference_frame;
  const frames = { reference_frame: sourceFrame, target_frame: role.target_frame };
  compare(declarations.get(positionChannel), {
    ...frames,
    position_unit: role.position.unit,
  });
  compare(declarations.get(orientationChannel), {
    ...frames,
    rotation: rotationName(role.orientation),
  });
  return [...mismatches];
}

// ── 같음 ───────────────────────────────────────────────────────────────────

/** JSON 값의 구조적 같음. 객체 키 순서는 보지 않는다. */
function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((item, index) => sameValue(item, b[index]))
    );
  }
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) {
    return false;
  }
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = Object.keys(left);
  return (
    keys.length === Object.keys(right).length &&
    keys.every((key) => key in right && sameValue(left[key], right[key]))
  );
}

/** 두 매핑이 같은지. 편집본이 저장본과 다른지(저장하지 않은 변경), 결과가 지난 매핑의 것인지에 쓴다. */
export function sameMapping(a: Mapping, b: Mapping): boolean {
  return sameValue(a, b);
}
